import { useEffect, useState } from "react";
import root from "../assets/styles/Root.module.css";
import styles from "../assets/styles/PrizeRevealAnimation.module.css";

export default function PrizeRevealAnimation({ 
   open, 
   onClose, 
   prizeAmount, 
   prizeTitle,
   hasBonus,
   bonusType,
   bonusMultiplier,
   bonusPrizeInfo = null, // Новое поле для бонуса-приза
   bonusDescription,
   baseAmount,
   bonusSpins = null,
   isExtraSpin = false
}) {
   const [isVisible, setIsVisible] = useState(false);
   const [showPrize, setShowPrize] = useState(false);
   const [showBonus, setShowBonus] = useState(false);
   const [showBonusSpins, setShowBonusSpins] = useState(false);

   useEffect(() => {
      if (open) {
         setIsVisible(true);
         const timers = [];
         
         // Анимация появления суммы
         timers.push(setTimeout(() => setShowPrize(true), 300));
         
         // Анимация появления бонуса (если есть)
         if (hasBonus) {
            timers.push(setTimeout(() => setShowBonus(true), 1000));
         }
         
         // Анимация появления дополнительных круток (если есть)
         if (bonusSpins && Array.isArray(bonusSpins) && bonusSpins.length > 0) {
            timers.push(setTimeout(() => setShowBonusSpins(true), 1500));
         }
         
         return () => {
            timers.forEach(timer => clearTimeout(timer));
         };
      } else {
         setIsVisible(false);
         setShowPrize(false);
         setShowBonus(false);
         setShowBonusSpins(false);
      }
   }, [open, hasBonus, bonusSpins]);

   const handleClose = () => {
      setIsVisible(false);
      setShowPrize(false);
      setShowBonus(false);
      setTimeout(() => onClose(), 300);
   };

   if (!open && !isVisible) return null;

   const getBonusText = () => {
      if (bonusType === "multiplier" && bonusMultiplier) {
         return `x${bonusMultiplier}`;
      } else if (bonusType === "prize" && bonusPrizeInfo) {
         // Новый тип бонуса - дополнительный приз
         return `+${bonusPrizeInfo.prize_name || 'Приз'} ($${Number(bonusPrizeInfo.amount || 0).toFixed(2)})`;
      } else if (bonusType === "extra_open") {
         return "Дополнительное открытие!";
      } else if (bonusDescription) {
         return bonusDescription;
      }
      return "Бонус!";
   };

   // Вычисляем общую сумму дополнительных круток
   const totalBonusSpinsAmount = bonusSpins && Array.isArray(bonusSpins) 
      ? bonusSpins.reduce((sum, spin) => sum + (Number(spin.amount) || 0), 0)
      : 0;

   return (
      <div 
         className={`${styles.overlay} ${isVisible ? styles.visible : ''}`}
         onClick={handleClose}
      >
         <div 
            className={`${styles.container} ${isVisible ? styles.visible : ''}`}
            onClick={(e) => e.stopPropagation()}
         >
            {/* Конфетти эффект */}
            <div className={styles.confetti}>
               {[...Array(50)].map((_, i) => (
                  <div 
                     key={i} 
                     className={styles.confettiPiece}
                     style={{
                        left: `${Math.random() * 100}%`,
                        animationDelay: `${Math.random() * 0.5}s`,
                        backgroundColor: ['#FF6B6B', '#4ECDC4', '#FFE66D', '#FF6B9D', '#C44569', '#64acff'][Math.floor(Math.random() * 6)]
                     }}
                  />
               ))}
            </div>

            {/* Основной приз */}
            <div className={`${styles.prizeContainer} ${showPrize ? styles.visible : ''}`}>
               {isExtraSpin && (
                  <div className={styles.extraSpinBadge}>🔄 ДОПОЛНИТЕЛЬНОЕ ОТКРЫТИЕ</div>
               )}
               <div className={styles.prizeLabel}>
                  {isExtraSpin ? 'Выпало в доп. крутке:' : 'Выпало:'}
               </div>
               
               {/* Если есть начальный выигрыш или дополнительные крутки, показываем разбивку */}
               {!isExtraSpin && (baseAmount || totalBonusSpinsAmount > 0) ? (
                  <>
                     <div className={styles.prizeBreakdown}>
                        {baseAmount && (
                           <div className={styles.prizeBreakdownRow}>
                              <span className={styles.prizeBreakdownLabel}>Начальный выигрыш:</span>
                              <span className={styles.prizeBreakdownAmount}>${Number(baseAmount).toFixed(2)}</span>
                           </div>
                        )}
                        {totalBonusSpinsAmount > 0 && (
                           <div className={styles.prizeBreakdownRow}>
                              <span className={styles.prizeBreakdownLabel}>Дополнительно:</span>
                              <span className={styles.prizeBreakdownAmount}>+${totalBonusSpinsAmount.toFixed(2)}</span>
                           </div>
                        )}
                        {(baseAmount || totalBonusSpinsAmount > 0) && (
                           <div className={styles.prizeBreakdownDivider}></div>
                        )}
                        <div className={styles.prizeBreakdownRow}>
                           <span className={styles.prizeBreakdownLabel}>Итого:</span>
                           <span className={styles.prizeBreakdownAmountTotal}>
                              ${typeof prizeAmount === 'number' || (typeof prizeAmount === 'string' && prizeAmount !== '?') 
                                 ? Number(prizeAmount).toFixed(2) 
                                 : prizeAmount}
                           </span>
                        </div>
                     </div>
                  </>
               ) : (
                  <div className={styles.prizeAmount}>
                     ${typeof prizeAmount === 'number' || (typeof prizeAmount === 'string' && prizeAmount !== '?') 
                        ? Number(prizeAmount).toFixed(2) 
                        : prizeAmount}
                  </div>
               )}
               
               {prizeTitle && (
                  <div className={styles.prizeTitle}>{prizeTitle}</div>
               )}
            </div>

            {/* Бонус */}
            {hasBonus && (
               <div className={`${styles.bonusContainer} ${showBonus ? styles.visible : ''}`}>
                  <div className={styles.bonusIcon}>🎉</div>
                  <div className={styles.bonusLabel}>БОНУС</div>
                  <div className={styles.bonusText}>{getBonusText()}</div>
                  {baseAmount && bonusType === "multiplier" && (
                     <div className={styles.bonusDetails}>
                        Начальный: ${Number(baseAmount).toFixed(2)} → Итоговый: {
                           typeof prizeAmount === 'number' || (typeof prizeAmount === 'string' && prizeAmount !== '?') 
                              ? Number(prizeAmount).toFixed(2) 
                              : prizeAmount
                        }
                     </div>
                  )}
                  {bonusType === "prize" && bonusPrizeInfo && (
                     <div className={styles.bonusDetails}>
                        Дополнительный приз: <strong>{bonusPrizeInfo.prize_name || 'Приз'}</strong> (+${Number(bonusPrizeInfo.amount || 0).toFixed(2)})
                     </div>
                  )}
                  {baseAmount && bonusType === "extra_open" && totalBonusSpinsAmount === 0 && (
                     <div className={styles.bonusDetails}>
                        Начальный выигрыш: ${Number(baseAmount).toFixed(2)}
                     </div>
                  )}
               </div>
            )}

            {/* Список дополнительных круток */}
            {bonusSpins && Array.isArray(bonusSpins) && bonusSpins.length > 0 && (
               <div className={`${styles.bonusSpinsContainer} ${showBonusSpins ? styles.visible : ''}`}>
                  <div className={styles.bonusSpinsTitle}>
                     🎁 Дополнительные открытия ({bonusSpins.length})
                  </div>
                  <div className={styles.bonusSpinsList}>
                     {bonusSpins.map((spin, idx) => (
                        <div 
                           key={spin.bonus_spin_id || spin.spin_id || spin.spinId || spin.id || idx} 
                           className={styles.bonusSpinItem}
                           style={{ animationDelay: `${idx * 0.1}s` }}
                        >
                           <span className={styles.bonusSpinNumber}>#{idx + 1}</span>
                           <span className={styles.bonusSpinAmount}>
                              +${Number(spin.amount || 0).toFixed(2)}
                           </span>
                        </div>
                     ))}
                  </div>
                  {totalBonusSpinsAmount > 0 && (
                     <div className={styles.bonusSpinsTotal}>
                        Всего дополнительно: <b>${totalBonusSpinsAmount.toFixed(2)}</b>
                     </div>
                  )}
               </div>
            )}

            {/* Кнопка закрытия */}
            <button 
               className={`${root.btn} ${styles.closeBtn}`}
               onClick={handleClose}
            >
               Закрыть
            </button>
         </div>
      </div>
   );
}

